import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { theme } from '../config/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: var(--nav-height, 128px);
  box-sizing: border-box;
`;

const ClassOptionsContainer = styled.div`
  flex: 1;
  display: flex;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const ClassOption = styled(Link)`
  width: 33.333%;
  height: calc(100vh - var(--nav-height, 128px));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  position: relative;
  transition: all 0.4s ease;
  background-color: ${theme.colors.primary.background};
  color: ${theme.colors.primary.text};

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.accent.main};
  }

  &:hover {
    background-color: ${theme.colors.accent.main};
    color: ${theme.colors.neutral.lightest};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100%;
    height: calc((100vh - var(--nav-height, 128px)) / 3);
    
    &:not(:last-child) {
      border-right: none;
      border-bottom: 1px solid ${theme.colors.accent.main};
    }
  }
`;

const OptionContent = styled(motion.div)`
  text-align: center;
  padding: ${theme.spacing.xl};
  max-width: 400px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    max-width: 90%;
  }
`;

const OptionTitle = styled.h2`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: ${theme.spacing.lg};
  letter-spacing: 0.02em;
  color: inherit;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
    margin-bottom: ${theme.spacing.md};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: ${theme.spacing.sm};
  }
`;

const OptionDescription = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 350px;
  color: inherit;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 100%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const BookClassPage = () => {
  const contentVariants = {
    hidden: { opacity: 0, y: theme.animations.moveMedium },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.medium,
        ease: 'easeOut',
      },
    },
  };

  return (
    <PageContainer>
      <ClassOptionsContainer>
        <ClassOption to="/book/group">
          <OptionContent
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <OptionTitle>Group Classes</OptionTitle>
            <OptionDescription>
              Join our energizing group sessions where you'll practice alongside others in a supportive, 
              community-focused environment. Perfect for motivation and shared energy.
            </OptionDescription>
          </OptionContent>
        </ClassOption>

        <ClassOption to="/book/private">
          <OptionContent
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <OptionTitle>Private Classes</OptionTitle>
            <OptionDescription>
              Experience personalized attention with one-on-one sessions tailored specifically to your 
              goals, needs, and pace. Ideal for focused progress and individual guidance.
            </OptionDescription>
          </OptionContent>
        </ClassOption>

        <ClassOption to="/book/events">
          <OptionContent
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <OptionTitle>Events</OptionTitle>
            <OptionDescription>
              Join our special workshops, retreats, and community events designed to deepen your 
              practice and connect with like-minded individuals.
            </OptionDescription>
          </OptionContent>
        </ClassOption>
      </ClassOptionsContainer>
      <Footer />
    </PageContainer>
  );
};

export default BookClassPage;